export type NotificationPermissionStatus = "granted" | "denied" | "default";

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  duration?: number; // Duration in milliseconds, persistent by default
  onClick?: () => void;
}

class BrowserNotificationManager {
  private static instance: BrowserNotificationManager;
  private permissionStatus: NotificationPermissionStatus = "default";

  static getInstance(): BrowserNotificationManager {
    if (!BrowserNotificationManager.instance) {
      BrowserNotificationManager.instance = new BrowserNotificationManager();
    }
    return BrowserNotificationManager.instance;
  }

  async requestPermission(): Promise<NotificationPermissionStatus> {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return "denied";
    }

    if (Notification.permission === "granted") {
      this.permissionStatus = "granted";
      return "granted";
    }

    if (Notification.permission === "denied") {
      this.permissionStatus = "denied";
      return "denied";
    }

    // Request permission with a persuasive message
    const permission = await Notification.requestPermission();
    this.permissionStatus = permission as NotificationPermissionStatus;
    return this.permissionStatus;
  }

  async requestPermissionWithMessage(): Promise<NotificationPermissionStatus> {
    return await this.requestPermission();
  }

  getPermissionStatus(): NotificationPermissionStatus {
    if ("Notification" in window) {
      this.permissionStatus =
        Notification.permission as NotificationPermissionStatus;
    }
    return this.permissionStatus;
  }

  isSupported(): boolean {
    return "Notification" in window;
  }

  showNotification(options: BrowserNotificationOptions): void {
    if (this.permissionStatus !== "granted") {
      console.warn("Notification permission not granted");
      return;
    }

    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return;
    }

    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || "/favicon.ico",
      tag: options.tag || "weddn-notification",
      badge: "/favicon.ico",
      requireInteraction: false,
    });

    // Handle click event
    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) {
        options.onClick();
      }
    };

    // Auto close only if duration is specified, otherwise persistent
    if (options.duration) {
      setTimeout(() => {
        notification.close();
      }, options.duration);
    }
  }
}

export const browserNotificationManager =
  BrowserNotificationManager.getInstance();
